<template>
  <teleport to="#modal-container">
    <transition appear :duration="150">
      <div :class="['modal bg-base-300/90', { 'modal-open': showContent }]">
        <div
          v-if="showContent"
          ref="target"
          :class="[
            contentClass,
            'modal-box w-full overflow-y-auto rounded bg-white shadow-lg transition initial:p-5',
            {
              'max-w-6xl': size === 'xl',
              'max-w-4xl': size === 'large',
              'max-w-xl': size === 'medium',
              'max-w-md': size === 'small',
              'h-full': height === 'full',
            },
          ]"
        >
          <m-heading v-if="title" theme="h2" :position="titlePosition">{{ title }}</m-heading>
          <m-dot-loader v-if="loading" class="py-16" />

          <div v-else class="h-full">
            <slot name="header"></slot>
            <slot />
            <m-action-list v-if="$slots.actions" class="pt-3">
              <slot name="actions"></slot>
            </m-action-list>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts">
  /**
   * Module-scoped, reference-counted body scroll lock shared by every MModal
   * instance. Using a counter (instead of each modal blindly setting/clearing
   * `document.body.style.overflow`) means overlapping or rapidly mounting/
   * unmounting modals can never leave the page permanently scroll-locked.
   */
  let scrollLockCount = 0;

  function lockBodyScroll() {
    if (scrollLockCount++ === 0) {
      document.body.style.overflow = 'hidden';
    }
  }

  function unlockBodyScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
      document.body.style.overflow = '';
    }
  }
</script>

<script lang="ts" setup>
  import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
  import { onClickOutside, onKeyStroke } from '@vueuse/core';
  import type { MHeading_PositionProp } from '@core/components/MHeading';
  import MDotLoader from '@core/components/MDotLoader/MDotLoader.vue';

  export interface Props {
    title?: string | null;
    titlePosition?: MHeading_PositionProp;
    form?: boolean;
    size?: 'small' | 'medium' | 'large' | 'xl';
    disableClickOutSide?: boolean;
    disableCloseOnEscape?: boolean;
    contentClass?: string;
    /**
     * Whether or not to show the loading indicator
     * Hides Content untile loading is complete
     */
    loading?: boolean;
    height?: 'defualt' | 'full';
  }
  export interface Emits {
    (e: 'close'): void;
    (e: 'submit'): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    size: 'medium',
    form: true,
    title: null,
    titlePosition: 'start',
    disableClickOutSide: false,
    disableCloseOnEscape: false,
    loading: false,
    contentClass: undefined,
    height: 'defualt',
  });
  const emit = defineEmits<Emits>();

  const showContent = ref(false);
  const target = ref(null);

  onClickOutside(target, () => {
    if (props.disableClickOutSide) return;
    showContent.value = false;
    emit('close');
  });

  onKeyStroke('Escape', () => {
    if (props.disableCloseOnEscape) return;
    showContent.value = false;
    emit('close');
  });

  onMounted(async () => {
    lockBodyScroll();
    // Reveal the content once the instance is mounted rather than waiting on the
    // transition's `@after-enter` callback. That callback can be interrupted or
    // never fire when a heavy child renders synchronously, which previously left
    // the overlay mounted (and the body scroll-locked) with no visible content.
    await nextTick();
    showContent.value = true;
  });

  onBeforeUnmount(() => {
    unlockBodyScroll();
  });
</script>
<style>
  .modal {
    @apply bg-customer-card bg-opacity-90;
  }
</style>
